/**
 * 渐变**读取**（纯函数、零副作用）。
 *
 * 渐变在文档里就是 `<linearGradient>` / `<radialGradient>` + 元素上写 `fill="url(#id)"`，
 * 所以这一层只做"把文档里已有的渐变读成结构化数据"，写入全在 `core/ops/gradient.ts`。
 *
 * 处理三个容易踩的点：
 *  1) `url(#id)` 里可能有引号、也可能带 fallback（`url(#a) none`）→ 统一用正则取；
 *  2) 色标可能写在 `xlink:href` 指向的另一个渐变上（SVG 1.1 继承）→ 跟一层；
 *  3) `gradientUnits` 可能是 `userSpaceOnUse`（绝对坐标）→ **原样保留**，不强行折算成 bbox。
 */
import { walk } from '../dom';

export type GradientType = 'linear' | 'radial';
export type GradientUnits = 'objectBoundingBox' | 'userSpaceOnUse';

export interface GradientStop {
  /** 归一化色值 `#rrggbb` */
  color: string;
  /** 0–1 */
  offset: number;
  /** 0–1，默认 1 */
  opacity: number;
}

export interface GradientInfo {
  id: string;
  node: Element;
  type: GradientType;
  units: GradientUnits;
  stops: GradientStop[];
  /** 线性方向角（度）：0 = 从左到右，顺时针为正（90 = 从上到下） */
  angle: number;
  /** 线性向量长度（bbox 单位；作者手写 0,0→1,1 这种会比 1 长，原样保留） */
  length: number;
  /** 径向的 cx / cy / r（原单位下的数值；bbox 时是 0–1） */
  radial: { cx: number; cy: number; r: number };
  /** 文档里引用它的元素数 */
  used: number;
}

const URL_RE = /url\(\s*['"]?#([^'")\s]+)['"]?\s*\)/;
const STOP_TAG = 'stop';

/** `url(#id)` → `id`；不是引用（纯色 / none / 空）→ null */
export function gradientIdOf(value: string | null | undefined): string | null {
  if (!value) return null;
  const m = URL_RE.exec(value);
  return m?.[1] ?? null;
}

/**
 * 按 id 找元素。
 * 刻意不用 `querySelector('#id')`：id 里凡是 CSS 特殊字符（`.`/`:`/数字开头）都会让选择器抛错。
 */
export function findById(root: Element, id: string): Element | null {
  if (!id) return null;
  let found: Element | null = null;
  walk(root, (el, path) => {
    if (!found && path.length > 0 && el.getAttribute('id') === id) found = el;
  });
  return found;
}

/** 文档里引用该 id 的元素数（`url(#id)` 出现在 fill/stroke/clip-path 等任意属性都算） */
export function countRefs(root: Element, id: string): number {
  const token = `url(#${id})`;
  let n = 0;
  walk(root, (el, path) => {
    if (path.length === 0) return;
    for (const attr of Array.from(el.attributes)) {
      if (attr.value.includes(token)) {
        n += 1;
        break;
      }
    }
  });
  return n;
}

/** 读某元素 fill/stroke 上的渐变；不是渐变引用 → null */
export function readGradient(root: Element, el: Element, which: 'fill' | 'stroke' = 'fill'): GradientInfo | null {
  const id = gradientIdOf(el.getAttribute(which) ?? styleOf(el, which));
  return id ? readGradientById(root, id) : null;
}

/** 按 id 读出完整渐变信息 */
export function readGradientById(root: Element, id: string): GradientInfo | null {
  const node = findById(root, id);
  if (!node) return null;
  const tag = node.tagName.toLowerCase();
  if (tag !== 'lineargradient' && tag !== 'radialgradient') return null;
  const type: GradientType = tag === 'lineargradient' ? 'linear' : 'radial';
  const units: GradientUnits =
    node.getAttribute('gradientUnits') === 'userSpaceOnUse' ? 'userSpaceOnUse' : 'objectBoundingBox';

  const x1 = num(node, 'x1', 0);
  const y1 = num(node, 'y1', 0);
  const x2 = num(node, 'x2', 1);
  const y2 = num(node, 'y2', 0);
  const dx = x2 - x1;
  const dy = y2 - y1;

  return {
    id,
    node,
    type,
    units,
    stops: readStops(node),
    angle: normalizeAngle((Math.atan2(dy, dx) * 180) / Math.PI),
    length: Math.max(Math.hypot(dx, dy), 0.0001),
    radial: { cx: num(node, 'cx', 0.5), cy: num(node, 'cy', 0.5), r: num(node, 'r', 0.5) },
    used: countRefs(root, id),
  };
}

/**
 * 读色标（含 `xlink:href` 继承一层）。
 * 注意：**不能用 `document.getElementById`** —— XML/SVG 文档里 jsdom（以及部分浏览器）
 * 只对 DTD 声明的 id 建索引，`<linearGradient id="x">` 查不到。所以按属性遍历找。
 */
export function readStops(node: Element): GradientStop[] {
  const own = stopChildren(node);
  if (own.length > 0) return own;
  const ref = hrefOf(node)?.replace(/^#/, '') ?? ''; // href 值形如 `#base`，要去掉 #
  if (!ref) return [];
  const docRoot = node.ownerDocument?.documentElement ?? null;
  const target = docRoot ? findById(docRoot, ref) : null;
  return target ? stopChildren(target) : [];
}

/** 预览用 CSS（属性面板那个小色条）—— 数值是 bbox 空间的近似，够看就行 */
export function gradientCss(info: GradientInfo): string {
  const list = info.stops
    .slice()
    .sort((a, b) => a.offset - b.offset)
    .map((s) => `${withAlpha(s.color, s.opacity)} ${(clamp01(s.offset) * 100).toFixed(1)}%`)
    .join(', ');
  if (info.type === 'radial') {
    return `radial-gradient(circle at ${(info.radial.cx * 100).toFixed(1)}% ${(info.radial.cy * 100).toFixed(1)}%, ${list})`;
  }
  // CSS 的 0deg 朝上、顺时针；SVG 这边 0deg 朝右 → +90 对齐
  return `linear-gradient(${((info.angle + 90) % 360).toFixed(0)}deg, ${list})`;
}

/* ------------------------------------------------------------------ 内部工具 */

/** 元素内联 style 里的某个属性（简化版：只看 `name: value;` 形式） */
function styleOf(el: Element, name: string): string | null {
  const m = new RegExp(`(?:^|;)\\s*${name}\\s*:\\s*([^;]+)`, 'i').exec(el.getAttribute('style') ?? '');
  return m?.[1]?.trim() ?? null;
}

/** 属性值可能是 `50%` / `0.5` → 都要能读成数 */
function num(node: Element, name: string, fallback: number): number {
  const raw = node.getAttribute(name);
  if (raw === null) return fallback;
  const trimmed = raw.trim();
  const value = trimmed.endsWith('%') ? Number.parseFloat(trimmed) / 100 : Number.parseFloat(trimmed);
  return Number.isFinite(value) ? value : fallback;
}

function stopChildren(node: Element): GradientStop[] {
  const out: GradientStop[] = [];
  for (const child of Array.from(node.children)) {
    if (child.tagName.toLowerCase() !== STOP_TAG) continue;
    out.push({
      color: (styleOf(child, 'stop-color') ?? child.getAttribute('stop-color') ?? '#000000').trim(),
      offset: clamp01(num(child, 'offset', 0)),
      opacity: clamp01(num(child, 'stop-opacity', 1)),
    });
  }
  return out;
}

function hrefOf(node: Element): string | null {
  return node.getAttribute('href') ?? node.getAttribute('xlink:href') ?? null;
}

const clamp01 = (n: number): number => (Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : 0);

/** 0–360 */
export function normalizeAngle(deg: number): number {
  const a = ((deg % 360) + 360) % 360;
  return Math.abs(a - 360) < 0.01 ? 0 : a;
}

/** 给预览用：色值 + 透明度 → `rgba()`（不必引入颜色模块） */
function withAlpha(hex: string, alpha: number): string {
  if (alpha >= 1) return hex;
  const m = /^#([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return hex;
  const n = Number.parseInt(m[1] as string, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}
